import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  Delete,
  Psychology,
  History,
  CheckCircle,
  HelpOutline,
  Save,
  Edit,
} from "@mui/icons-material";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";
import { extractDiagnosticSuspicionHistory } from "@/fluxvet/utils/questionnaireResponseHelper";
import { HistoryModal } from "./HistoryModal";

/**
 * Gera um ID único para os itens
 */
const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// =============================================================================
// TIPOS - Interfaces
// =============================================================================

interface DiagnosticSuspicion {
  id: string;
  description: string;
  status?: "confirmed" | "not_confirmed"; // Status da suspeita: confirmado ou não confirmado
  confirmationDescription?: string; // Descrição adicional (sempre disponível)
}

interface DiagnosticSuspicionsSectionProps {
  control: any;
  watch: any;
  setValue: any;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente de Suspeitas Diagnósticas
 * Permite adicionar, editar e remover múltiplas suspeitas diagnósticas
 * Similar ao TreatmentsSection, com formulário à esquerda e lista à direita
 */
export const DiagnosticSuspicionsSection: React.FC<
  DiagnosticSuspicionsSectionProps
> = ({ control, watch, setValue }) => {
  // Busca o histórico de QuestionnaireResponse do contexto
  const { historyQuestionnaireResponse } = useVetAttendance();

  // Processa o histórico para extrair dados
  const diagnosticSuspicionHistory = useMemo(
    () => extractDiagnosticSuspicionHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  // Observa a lista do formulário
  const diagnosticSuspicions: DiagnosticSuspicion[] =
    watch("diagnosticSuspicions") || [];

  // Estado para o formulário de adição (apenas descrição)
  const [suspicionFormData, setSuspicionFormData] = useState({
    description: "",
  });

  // Estado para controlar quais cards estão em modo de edição
  const [editingCardIds, setEditingCardIds] = useState<Set<string>>(new Set());

  // Estado para controlar valores temporários de edição em cada card
  // Armazena os valores locais antes de salvar
  const [cardEditStates, setCardEditStates] = useState<
    Record<
      string,
      {
        status?: "confirmed" | "not_confirmed";
        confirmationDescription: string;
      }
    >
  >({});

  // Estado para controlar o modal de histórico
  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
  });

  /**
   * Adiciona nova suspeita diagnóstica à lista
   * Nova suspeita sempre começa como "suspected"
   */
  const handleAdd = () => {
    if (!suspicionFormData.description.trim()) {
      return;
    }

    const newSuspicion: DiagnosticSuspicion = {
      id: generateId(),
      description: suspicionFormData.description,
      // Não define status inicial - será definido pelo usuário
    };

    setValue("diagnosticSuspicions", [...diagnosticSuspicions, newSuspicion]);
    resetForm();
  };

  /**
   * Obtém o estado de edição de um card
   * Se não existir no estado, retorna os valores atuais da suspeita
   */
  const getCardEditState = (suspicion: DiagnosticSuspicion) => {
    if (!cardEditStates[suspicion.id]) {
      // Retorna valores padrão baseados na suspeita atual
      return {
        status: suspicion.status,
        confirmationDescription: suspicion.confirmationDescription || "",
      };
    }
    return cardEditStates[suspicion.id];
  };

  /**
   * Atualiza o estado temporário de edição de um card
   */
  const updateCardEditState = (
    id: string,
    updates: Partial<{
      status?: "confirmed" | "not_confirmed";
      confirmationDescription: string;
    }>
  ) => {
    setCardEditStates((prev) => ({
      ...prev,
      [id]: {
        ...getCardEditState(
          diagnosticSuspicions.find((s) => s.id === id) || {
            id,
            description: "",
          }
        ),
        ...updates,
      },
    }));
  };

  /**
   * Inicia a edição de um card
   */
  const handleStartEdit = (id: string) => {
    setEditingCardIds((prev) => new Set(prev).add(id));
    // Inicializa o estado de edição se não existir
    const suspicion = diagnosticSuspicions.find((s) => s.id === id);
    if (suspicion && !cardEditStates[id]) {
      setCardEditStates((prev) => ({
        ...prev,
        [id]: {
          status: suspicion.status,
          confirmationDescription: suspicion.confirmationDescription || "",
        },
      }));
    }
  };

  /**
   * Cancela a edição de um card
   */
  const handleCancelEdit = (id: string) => {
    setEditingCardIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    // Remove o estado temporário
    setCardEditStates((prev) => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
  };

  /**
   * Salva as alterações de um card no formulário
   */
  const handleSaveCard = (id: string) => {
    const editState = cardEditStates[id];
    if (!editState) return;

    const updatedSuspicions = diagnosticSuspicions.map((suspicion) =>
      suspicion.id === id
        ? {
            ...suspicion,
            status: editState.status,
            confirmationDescription: editState.confirmationDescription,
          }
        : suspicion
    );

    setValue("diagnosticSuspicions", updatedSuspicions);
    // Remove o estado temporário e sai do modo de edição
    setCardEditStates((prev) => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
    setEditingCardIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  /**
   * Remove uma suspeita da lista
   */
  const handleDelete = (id: string) => {
    const filtered = diagnosticSuspicions.filter(
      (suspicion) => suspicion.id !== id
    );
    setValue("diagnosticSuspicions", filtered);
  };

  /**
   * Reseta o formulário de adição
   */
  const resetForm = () => {
    setSuspicionFormData({
      description: "",
    });
  };

  return (
    <>
      <Box>
        {/* Cabeçalho */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Psychology sx={{ color: "#00A884" }} />
            <Typography variant="h6" fontWeight={600}>
              Suspeitas Diagnósticas
            </Typography>
            {diagnosticSuspicions.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                ({diagnosticSuspicions.length}{" "}
                {diagnosticSuspicions.length === 1 ? "item" : "itens"})
              </Typography>
            )}
          </Stack>
          <Button
            size="small"
            startIcon={<History />}
            onClick={() => setHistoryModal({ isOpen: true })}
            sx={{
              textTransform: "none",
              fontSize: "0.85rem",
              color: "#666",
              "&:hover": {
                color: "#00A884",
                bgcolor: "rgba(0, 168, 132, 0.08)",
              },
            }}
          >
            Ver histórico
          </Button>
        </Stack>

        <Grid container spacing={2}>
          {/* Coluna Esquerda: Formulário (40%) */}
          <Grid item xs={12} md={4.8}>
            <Paper
              key={`suspicion-form-${diagnosticSuspicions.length}`}
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                bgcolor: "#fafafa",
                height: "100%",
                minHeight: 420,
              }}
            >
              <Stack spacing={2} height="100%">
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="#00A884"
                >
                  ➕ Nova Suspeita
                </Typography>

                <Divider />

                <TextField
                  fullWidth
                  label="Descrição da suspeita diagnóstica *"
                  placeholder="Ex: Gastroenterite aguda, possivelmente relacionada a mudança alimentar..."
                  value={suspicionFormData.description}
                  onChange={(e) =>
                    setSuspicionFormData({
                      description: e.target.value,
                    })
                  }
                  multiline
                  rows={10}
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#fff",
                    },
                  }}
                />

                <Box sx={{ flexGrow: 1 }} />

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAdd}
                    sx={{
                      bgcolor: "#00A884",
                      "&:hover": { bgcolor: "#008c6e" },
                      textTransform: "none",
                    }}
                  >
                    Adicionar
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* Coluna Direita: Lista de itens (60%) */}
          <Grid item xs={12} md={7.2}>
            {diagnosticSuspicions.length > 0 ? (
              <Stack spacing={2}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  fontWeight={600}
                >
                  SUSPEITAS DIAGNÓSTICAS REGISTRADAS
                </Typography>
                <Box
                  sx={{
                    maxHeight: 420,
                    overflowY: "auto",
                    pr: 0.5,
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      bgcolor: "#f1f1f1",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: "#00A884",
                      borderRadius: "10px",
                    },
                  }}
                >
                  <Stack spacing={2}>
                    {diagnosticSuspicions.map((suspicion) => {
                      const isEditing = editingCardIds.has(suspicion.id);
                      const editState = getCardEditState(suspicion);
                      const hasChanges =
                        isEditing &&
                        (editState.status !== suspicion.status ||
                          editState.confirmationDescription !==
                            (suspicion.confirmationDescription || ""));

                      return (
                        <Card
                          key={suspicion.id}
                          sx={{
                            border: "1px solid #e0e0e0",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: "#00A884",
                              boxShadow: "0 4px 12px rgba(0, 168, 132, 0.15)",
                            },
                          }}
                        >
                          <CardContent>
                            <Stack spacing={2}>
                              {/* Cabeçalho com título e botões */}
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <Typography
                                    variant="h6"
                                    fontWeight={600}
                                    color="#00A884"
                                  >
                                    Suspeita #
                                    {diagnosticSuspicions.indexOf(suspicion) +
                                      1}
                                  </Typography>
                                  {/* Indicador visual de status atual */}
                                  {suspicion.status && (
                                    <Chip
                                      icon={
                                        suspicion.status === "confirmed" ? (
                                          <CheckCircle fontSize="small" />
                                        ) : (
                                          <HelpOutline fontSize="small" />
                                        )
                                      }
                                      label={
                                        suspicion.status === "confirmed"
                                          ? "Confirmado"
                                          : "Não Confirmado"
                                      }
                                      size="small"
                                      color={
                                        suspicion.status === "confirmed"
                                          ? "success"
                                          : "default"
                                      }
                                      sx={{
                                        height: 24,
                                        fontSize: "0.75rem",
                                        fontWeight: 500,
                                      }}
                                    />
                                  )}
                                </Stack>
                                <Stack direction="row" spacing={0.5}>
                                  {isEditing ? (
                                    <>
                                      {/* Botão de salvar - só aparece se houver mudanças */}
                                      {hasChanges && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          startIcon={<Save />}
                                          onClick={() =>
                                            handleSaveCard(suspicion.id)
                                          }
                                          sx={{
                                            bgcolor: "#00A884",
                                            "&:hover": { bgcolor: "#008c6e" },
                                            textTransform: "none",
                                            fontSize: "0.75rem",
                                            px: 1.5,
                                          }}
                                        >
                                          Salvar
                                        </Button>
                                      )}
                                      {/* Botão de cancelar */}
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() =>
                                          handleCancelEdit(suspicion.id)
                                        }
                                        sx={{
                                          textTransform: "none",
                                          fontSize: "0.75rem",
                                          px: 1.5,
                                        }}
                                      >
                                        Cancelar
                                      </Button>
                                    </>
                                  ) : (
                                    /* Botão de editar - aparece quando não está em modo de edição */
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<Edit />}
                                      onClick={() =>
                                        handleStartEdit(suspicion.id)
                                      }
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.75rem",
                                        px: 1.5,
                                        borderColor: "#00A884",
                                        color: "#00A884",
                                        "&:hover": {
                                          borderColor: "#008c6e",
                                          bgcolor: "rgba(0, 168, 132, 0.08)",
                                        },
                                      }}
                                    >
                                      Editar
                                    </Button>
                                  )}
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(suspicion.id)}
                                    sx={{
                                      color: "#666",
                                      "&:hover": {
                                        color: "#f44336",
                                        bgcolor: "rgba(244, 67, 54, 0.08)",
                                      },
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>

                              <Divider />

                              {/* Descrição da suspeita */}
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: "pre-wrap" }}
                              >
                                {suspicion.description}
                              </Typography>

                              {/* Seção de Status - só aparece quando está em modo de edição */}
                              {isEditing && (
                                <Box
                                  sx={{
                                    bgcolor: "rgba(0, 168, 132, 0.05)",
                                    p: 2,
                                    borderRadius: 1,
                                    border: "1px solid rgba(0, 168, 132, 0.2)",
                                  }}
                                >
                                  <Stack spacing={1.5}>
                                    <Typography
                                      variant="caption"
                                      fontWeight={600}
                                      color="#00A884"
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      Status da Suspeita:
                                    </Typography>
                                    <FormControl
                                      component="fieldset"
                                      size="small"
                                    >
                                      <RadioGroup
                                        row
                                        value={editState.status || ""}
                                        onChange={(e) =>
                                          updateCardEditState(suspicion.id, {
                                            status: e.target.value as
                                              | "confirmed"
                                              | "not_confirmed",
                                            // Mantém a descrição independente do status
                                            confirmationDescription:
                                              editState.confirmationDescription,
                                          })
                                        }
                                      >
                                        <FormControlLabel
                                          value="not_confirmed"
                                          control={<Radio size="small" />}
                                          label="Não Confirmado"
                                        />
                                        <FormControlLabel
                                          value="confirmed"
                                          control={<Radio size="small" />}
                                          label="Confirmado"
                                        />
                                      </RadioGroup>
                                    </FormControl>
                                  </Stack>
                                </Box>
                              )}

                              {/* Seção de Descrição de Confirmação - visível apenas em edição ou se preenchida */}
                              {(isEditing ||
                                suspicion.confirmationDescription ||
                                editState.confirmationDescription) && (
                                <Box
                                  sx={{
                                    bgcolor: "rgba(76, 175, 80, 0.08)",
                                    p: 2,
                                    borderRadius: 1,
                                    borderLeft: "3px solid #4caf50",
                                  }}
                                >
                                  <Stack spacing={1.5}>
                                    <Typography
                                      variant="caption"
                                      fontWeight={600}
                                      color="#4caf50"
                                    >
                                      Descrição da Confirmação:
                                    </Typography>
                                    <TextField
                                      fullWidth
                                      multiline
                                      rows={3}
                                      value={
                                        isEditing
                                          ? editState.confirmationDescription
                                          : suspicion.confirmationDescription ||
                                            ""
                                      }
                                      onChange={(e) => {
                                        if (isEditing) {
                                          updateCardEditState(suspicion.id, {
                                            confirmationDescription:
                                              e.target.value,
                                          });
                                        } else {
                                          // Se não está em modo de edição, salva diretamente
                                          const updatedSuspicions =
                                            diagnosticSuspicions.map((s) =>
                                              s.id === suspicion.id
                                                ? {
                                                    ...s,
                                                    confirmationDescription:
                                                      e.target.value,
                                                  }
                                                : s
                                            );
                                          setValue(
                                            "diagnosticSuspicions",
                                            updatedSuspicions
                                          );
                                        }
                                      }}
                                      placeholder="Ex: Confirmado através de exame de sangue que revelou aumento de enzimas hepáticas..."
                                      variant="outlined"
                                      size="small"
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          bgcolor: "#fff",
                                        },
                                      }}
                                    />
                                  </Stack>
                                </Box>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: "center",
                  bgcolor: "#fafafa",
                  border: "1px dashed #e0e0e0",
                  borderRadius: 2,
                  minHeight: 420,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Psychology sx={{ fontSize: 64, color: "#bdbdbd", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhuma suspeita diagnóstica adicionada
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Preencha o campo ao lado e clique em "Adicionar"
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Modal de histórico */}
      <HistoryModal
        open={historyModal.isOpen}
        onClose={() => setHistoryModal({ isOpen: false })}
        title="Histórico - Suspeitas Diagnósticas"
        history={diagnosticSuspicionHistory}
      />
    </>
  );
};

export default DiagnosticSuspicionsSection;
